import type { DomainError } from '@core/domain';
import { useStandardLabels, useCustomLabels } from '@system-config/label-management/hooks';
import { useMemo } from 'react';

import type { CombinedLabel } from '../domain/ModerationLabels';

/**
 * Combines standard and custom labels for moderation panel
 *
 * Re-uses existing hooks per project standards:
 * - useStandardLabels: Fetches standard labels from system-config
 * - useCustomLabels: Fetches custom labels for a community
 *
 * @param communityId - The ID of the community for custom labels
 * @returns Combined labels, loading state, and errors
 */
export function useCombinedLabels(communityId: string) {
  // ✅ Use existing hooks
  const { labels: customLabels, loading: customLoading, errors: customErrors } = useCustomLabels(communityId);
  const { labels: standardLabels, loading: standardLoading, errors: standardErrors } = useStandardLabels();

  const combinedLabels = useMemo(() => {
    // Map standard labels (already filtered by useStandardLabels)
    const standard: CombinedLabel[] = standardLabels.map(label => ({
      id: label.id,
      name: label.labelName,
      description: label.policy,
      contentType: label.contentType,
      source: 'standard' as const,
      isActive: label.labelStatus === 'Active',
      original: label,
    }));

    // Map custom labels
    const custom: CombinedLabel[] = customLabels
      .filter(label => label.active)
      .map(label => ({
        id: label.id,
        name: label.name,
        description: label.description,
        contentType: null,
        source: 'custom' as const,
        isActive: label.active,
        original: label,
      }));

    // Sort alphabetically by name
    return [...standard, ...custom].sort((a, b) => a.name.localeCompare(b.name));
  }, [standardLabels, customLabels]);

  // Combine errors
  const errors: DomainError[] | null = useMemo(() => {
    const allErrors = [...(customErrors || []), ...(standardErrors || [])];

    return allErrors.length > 0 ? allErrors : null;
  }, [customErrors, standardErrors]);

  return {
    labels: combinedLabels,
    loading: customLoading || standardLoading,
    errors,
  };
}
